import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  AfterViewInit,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { PeopleService } from '../../services/people.service';
import { AddressService } from '../../services/address.service';
import { ZipcodeResponse } from '../../model/zipcode-response';
import { Router } from '@angular/router';
import { PeopleBarramentoResponse } from '../../model/people-barramento-response';
import { CommonModule } from '@angular/common';
import { CpfValidator } from '../../utils/cpf-validator';
import { FormStateService } from '../../services/form-state.service';
import { CpfSharedService } from 'src/app/services/cpf-shared.service';
import { PhoneStateService } from '../../services/phone-state.service';
import { Subscription } from 'rxjs';

// PrimeNG imports - apenas o necessário
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { InputMaskModule } from 'primeng/inputmask';
import { FloatLabelModule } from 'primeng/floatlabel';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { DividerModule } from 'primeng/divider';
import { PanelModule } from 'primeng/panel';

@Component({
  selector: 'app-people',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    CardModule,
    ButtonModule,
    InputTextModule,
    InputMaskModule,
    FloatLabelModule,
    ToastModule,
    DividerModule,
    PanelModule,
  ],
  providers: [MessageService],
  template: `
    <p-toast></p-toast>
    @if(!person) {
    <p-card>
      <ng-template pTemplate="header">
        <div class="card-header">
          <h2><i class="pi pi-user"></i> Cadastro/Alteração de Pessoa</h2>
        </div>
      </ng-template>
      
      <div class="people-container">
        <form [formGroup]="peopleForm" (submit)="onSubmit()" class="people-form">
          
          <!-- Seção: Dados Pessoais -->
          <p-panel header="Dados Pessoais" [toggleable]="false" styleClass="compact-panel">
            <div class="form-grid">
              <!-- Primeira linha: CPF, Nome e Data de Nascimento -->
              <div class="form-row">
                <div class="form-field cpf-with-button">
                  <label for="cpf" class="field-label">CPF*</label>
                  <div class="cpf-input-group">
                    <p-inputMask
                      #cpf
                      id="cpf"
                      formControlName="cpf"
                      mask="999.999.999-99"
                      [ngClass]="{ 'p-invalid': isFieldInvalid('cpf') }"
                      (onComplete)="buscarCpf()"
                      (keyup.enter)="buscarCpf()"
                      [autoClear]="false"
                      styleClass="compact-input cpf-input">
                    </p-inputMask>
                    <p-button 
                      label="Buscar" 
                      icon="pi pi-search"
                      type="button"
                      (onClick)="buscarCpf()"
                      styleClass="cpf-search-button"
                      size="small">
                    </p-button>
                  </div>
                  <small *ngIf="isFieldInvalid('cpf')" class="p-error">CPF é obrigatório</small>
                </div>

                <div class="form-field">
                  <label for="name" class="field-label">Nome Completo*</label>
                  <input
                    pInputText
                    #nameInput
                    id="name"
                    formControlName="name"
                    [ngClass]="{ 'p-invalid': isFieldInvalid('name') }"
                    class="compact-input"
                    style="text-transform: uppercase"
                    maxlength="150"
                  />
                  <small *ngIf="isFieldInvalid('name')" class="p-error">Nome é obrigatório</small>
                </div>

                <div class="form-field">
                  <label for="birthDate" class="field-label">Data de Nascimento*</label>
                  <input
                    pInputText
                    #birthDate
                    id="birthDate"
                    type="date"
                    formControlName="birthDate"
                    [ngClass]="{ 'p-invalid': isFieldInvalid('birthDate') }"
                    class="compact-input"
                    (change)="calculateAge()"
                  />
                  <small *ngIf="isFieldInvalid('birthDate')" class="p-error">Data de nascimento é obrigatória</small>
                  <span class="age-display" *ngIf="age">{{ age }} anos</span>
                </div>
              </div>

              <div class="form-row">
                <div class="form-field phone-with-paste">
                  <label for="homePhone" class="field-label">Telefone</label>
                  <div class="phone-input-group">
                    <p-inputMask
                      id="homePhone"
                      formControlName="homePhone"
                      mask="(99) 9999-9999"
                      [autoClear]="false"
                      styleClass="compact-input phone-input">
                    </p-inputMask>
                    <button 
                      *ngIf="hasLastHomePhone"
                      type="button"
                      class="paste-phone-button"
                      (click)="pasteHomePhone()"
                      title="Colar telefone do último cadastro: \n {{ getLastHomePhoneFormatted() }}"
                      pButton
                      icon="pi pi-clone">
                    </button>
                  </div>
                </div>

                <div class="form-field phone-with-paste">
                  <label for="mobilePhone" class="field-label">Celular*</label>
                  <div class="phone-input-group">
                    <p-inputMask
                      #mobilePhone
                      id="mobilePhone"
                      formControlName="mobilePhone"
                      mask="(99) 99999-9999"
                      [ngClass]="{ 'p-invalid': isFieldInvalid('mobilePhone') }"
                      [autoClear]="false"
                      styleClass="compact-input phone-input">
                    </p-inputMask>
                    <button 
                      *ngIf="hasLastMobilePhone"
                      type="button"
                      class="paste-phone-button"
                      (click)="pasteMobilePhone()"
                      title="Colar celular do último cadastro: \n {{ getLastMobilePhoneFormatted() }}"
                      pButton
                      icon="pi pi-clone">
                    </button>
                  </div>
                  <small *ngIf="isFieldInvalid('mobilePhone')" class="p-error">Celular é obrigatório</small>
                </div>

                <div class="form-field">
                  <label for="email" class="field-label">E-mail</label>
                  <input
                    pInputText
                    id="email"
                    type="email"
                    formControlName="email"
                    class="compact-input"
                    style="text-transform: lowercase"
                  />
                </div>
              </div>
            </div>
          </p-panel>

          <!-- Seção: Endereço -->

          <p-panel header="Endereço" [toggleable]="false" styleClass="compact-panel">
            <div class="form-grid">

              <!-- BOTÕES - SALVAR E CARREGAR ENDEREÇO DO BENEFICIÁRIO -->
    <div class="same-address-button-container">
      <p-button
        label="Usar Endereço Salvo"
        icon="pi pi-copy"
        (onClick)="fillWithSavedAddress()"
        styleClass="p-button-secondary"
        pTooltip="Preencher com endereço salvo anteriormente"
         title="Colar último endereço salvo: \n {{ formatAddressForDisplay() }}"
      ></p-button>
    </div>
              <div class="form-row">
                <div class="form-field cep-with-button">
                  <label for="zipcode" class="field-label">CEP*</label>
                  <div class="cep-input-group">
                    <p-inputMask
                      #zipcode
                      id="zipcode"
                      formControlName="zipcode"
                      mask="99999-999"
                      [ngClass]="{ 'p-invalid': isFieldInvalid('zipcode') }"
                      (onComplete)="buscarCep()"
                      (keyup.enter)="buscarCep()"
                      [autoClear]="false"
                      styleClass="compact-input cep-input">
                    </p-inputMask>
                    <p-button 
                      label="Buscar" 
                      icon="pi pi-search"
                      type="button"
                      (onClick)="buscarCep()"
                      styleClass="cep-search-button"
                      size="small">
                    </p-button>
                  </div>
                  <small *ngIf="isFieldInvalid('zipcode')" class="p-error">CEP é obrigatório</small>
                </div>

                <div class="form-field">
                  <label for="street" class="field-label">Logradouro*</label>
                  <input
                    pInputText
                    id="street"
                    formControlName="street"
                    [ngClass]="{ 'p-invalid': isFieldInvalid('street') }"
                    class="compact-input"
                    style="text-transform: uppercase"
                  />
                  <small *ngIf="isFieldInvalid('street')" class="p-error">Logradouro é obrigatório</small>
                </div>

                <div class="form-field small">
                  <label for="number" class="field-label">Número*</label>
                  <input
                    pInputText
                    id="number"
                    formControlName="number"
                    [ngClass]="{ 'p-invalid': isFieldInvalid('number') }"
                    class="compact-input"
                    maxlength="10"
                  />
                  <small *ngIf="isFieldInvalid('number')" class="p-error">Número é obrigatório</small>
                </div>
              </div>

              <div class="form-row">
                <div class="form-field">
                  <label for="compliment" class="field-label">Complemento</label>
                  <input
                    pInputText
                    id="compliment"
                    formControlName="compliment"
                    class="compact-input"
                    style="text-transform: uppercase"
                  />
                </div>

                <div class="form-field">
                  <label for="neighborhood" class="field-label">Bairro*</label>
                  <input
                    pInputText
                    id="neighborhood"
                    formControlName="neighborhood"
                    [ngClass]="{ 'p-invalid': isFieldInvalid('neighborhood') }"
                    class="compact-input"
                    style="text-transform: uppercase"
                  />
                  <small *ngIf="isFieldInvalid('neighborhood')" class="p-error">Bairro é obrigatório</small>
                </div>

                <div class="form-field">
                  <label for="city" class="field-label">Cidade*</label>
                  <input
                    pInputText
                    id="city"
                    formControlName="city"
                    [ngClass]="{ 'p-invalid': isFieldInvalid('city') }"
                    class="compact-input"
                    style="text-transform: uppercase"
                  />
                  <small *ngIf="isFieldInvalid('city')" class="p-error">Cidade é obrigatória</small>
                </div>

                <div class="form-field small">
                  <label for="state" class="field-label">Estado*</label>
                  <input
                    pInputText
                    id="state"
                    formControlName="state"
                    [ngClass]="{ 'p-invalid': isFieldInvalid('state') }"
                    class="compact-input"
                    maxlength="2"
                    style="text-transform: uppercase"
                  />
                  <small *ngIf="isFieldInvalid('state')" class="p-error">Estado é obrigatório</small>
                </div>
              </div>
            </div>
          </p-panel>

          <!-- Botões -->
          <div class="form-actions">
            <p-button 
              label="Salvar" 
              icon="pi pi-save"
              type="submit"
              [disabled]="!peopleForm.valid"
              styleClass="save-button">
            </p-button>
            <p-button 
              label="Cancelar" 
              icon="pi pi-times"
              type="button"
              (onClick)="cancelar()"
              styleClass="cancel-button"
              severity="secondary">
            </p-button>
          </div>
        </form>
      </div>
    </p-card>
    } @else {
    <div class="success-message">
      <h3>Pessoa cadastrada com sucesso!</h3>
      <p-button label="Novo Cadastro" (onClick)="novoCadastro()" icon="pi pi-plus"></p-button>
    </div>
    }
  `,
  styles: [
    `
      /* Card Header - Simples como emission */
      .card-header {
        padding: 16px 16px 0;
        margin: 0;
      }

      .card-header h2 {
        color: #325565;
        font-weight: 600;
        font-size: 1.5rem;
        margin: 0;
        font-family: "Montserrat", sans-serif;
      }

      /* Container Principal */
      .people-container {
        padding: 10px;
      }

      .people-form {
        width: 100%;
      }

      /* Panels Compactos */
      :host ::ng-deep .compact-panel {
        margin-bottom: 1rem;
      }

      :host ::ng-deep .compact-panel .p-panel-header {
        background: rgba(255, 230, 203, 0.1);
        border: 1px solid #FFE6CB;
        color: #325565;
        font-weight: 600;
        padding: 0.75rem 1rem;
      }

      :host ::ng-deep .compact-panel .p-panel-content {
        padding: 1rem;
        border: 1px solid #FFE6CB;
        border-top: none;
      }

      /* Grid de Formulário */
      .form-grid {
        display: flex;
        flex-direction: column;
        gap: 1.5rem;
      }

      .form-row {
        display: grid;
        grid-template-columns: 1.2fr 2.5fr 1.3fr;
        gap: 1rem;
        align-items: start;
      }

      .form-row:has(.small) {
        grid-template-columns: 1fr 2fr 1fr 0.8fr;
      }

      .form-field {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
        min-height: 75px;
      }

      .form-field.small {
        max-width: 100px;
        min-height: 75px;
      }

      .field-label {
        font-weight: 600;
        color: #325565;
        font-size: 0.875rem;
        margin-bottom: 0.5rem;
        line-height: 1.2;
      }

      /* Inputs Compactos */
      :host ::ng-deep .compact-input {
        height: 3rem !important;
        font-size: 0.875rem;
        width: 100%;
      }

      :host ::ng-deep .compact-input .p-inputtext {
        height: 3rem !important;
        font-size: 0.875rem;
        padding: 0.5rem 0.75rem;
        width: 100%;
      }

      :host ::ng-deep input.compact-input {
        height: 3rem !important;
        font-size: 0.875rem;
        padding: 0.5rem 0.75rem !important;
        width: 100%;
      }

      :host ::ng-deep p-inputmask .p-inputtext {
        height: 3rem !important;
        padding: 0.5rem 0.75rem !important;
      }

      :host ::ng-deep .p-inputtext {
        height: 3rem !important;
        padding: 0.5rem 0.75rem !important;
      }

      /* Validação */
      :host ::ng-deep .p-invalid {
        border-color: #dc3545 !important;
        background-color: rgba(220, 53, 69, 0.1) !important;
      }

      :host ::ng-deep .p-error {
        color: #dc3545;
        font-size: 0.75rem;
        margin-top: 0.25rem;
      }

      /* Botões de Ação */
      .form-actions {
        display: flex;
        justify-content: flex-end;
        gap: 1rem;
        margin-top: 2rem;
        padding-top: 1rem;
        border-top: 1px solid #FFE6CB;
      }

      :host ::ng-deep .save-button .p-button {
        background: #F15A22;
        border-color: #F15A22;
        color: white;
        font-weight: 600;
      }

      :host ::ng-deep .save-button .p-button:hover {
        background: #e54a1b;
        border-color: #e54a1b;
      }

      :host ::ng-deep .save-button .p-button:disabled {
        background: #cccccc;
        border-color: #cccccc;
        color: #666666;
      }

      :host ::ng-deep .cancel-button .p-button {
        background: transparent;
        border-color: #666;
        color: #666;
      }

      :host ::ng-deep .cancel-button .p-button:hover {
        background: rgba(102, 102, 102, 0.1);
        border-color: #666;
        color: #666;
      }

      /* Mensagem de Sucesso */
      .success-message {
        text-align: center;
        padding: 2rem;
        background: rgba(40, 167, 69, 0.1);
        border: 1px solid #28a745;
        border-radius: 8px;
        color: #155724;
      }

      .success-message h3 {
        color: #155724;
        margin-bottom: 1rem;
      }

      /* Age display */
      .age-display {
        font-size: 0.875rem;
        color: #325565;
        font-weight: 500;
        margin-top: 0.25rem;
      }

      /* PrimeNG Card Customization - Igual ao emission */
      :host ::ng-deep p-card {
        width: 100%;
      }

      :host ::ng-deep .p-card {
        margin: 0;
        padding: 10px;
        background: white;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        border: none;
      }

      :host ::ng-deep .p-card .p-card-body {
        padding: 10px !important;
        margin: 0;
      }

      /* CPF Input Group */
      .form-field.cpf-with-button {
        grid-column: span 1;
      }

      .cpf-input-group {
        display: flex;
        gap: 0.5rem;
        align-items: stretch;
      }

      :host ::ng-deep .cpf-input {
        flex: 1;
      }

      /* CEP Input Group */
      .form-field.cep-with-button {
        grid-column: span 1;
      }

      .cep-input-group {
        display: flex;
        gap: 0.5rem;
        align-items: stretch;
      }

      :host ::ng-deep .cep-input {
        flex: 1;
      }

      :host ::ng-deep .cpf-search-button .p-button,
      :host ::ng-deep .cep-search-button .p-button {
        background: rgb(16, 148, 158) !important;
        border-color: rgb(16, 148, 158) !important;
        color: white !important;
        font-size: 0.875rem;
        padding: 0.5rem 1rem;
        height: 3rem;
        white-space: nowrap;
        font-weight: 600;
        display: flex;
        align-items: center;
        justify-content: center;
        box-sizing: border-box;
      }

      :host ::ng-deep .cpf-search-button .p-button:hover,
      :host ::ng-deep .cep-search-button .p-button:hover {
        background: rgb(6, 182, 212) !important;
        border-color: rgb(6, 182, 212) !important;
      }

      /* Phone Input Group - NOVO */
      .form-field.phone-with-paste {
        grid-column: span 1;
      }

      .phone-input-group {
        display: flex;
        gap: 0.5rem;
        align-items: stretch;
        position: relative;
      }

      :host ::ng-deep .phone-input {
        flex: 1;
      }

      /* Botão de Colar Telefone - NOVO */
      .paste-phone-button {
        background: #10949e !important;
        border: 1px solid #10949e !important;
        color: white !important;
        width: 3rem !important;
        height: 3rem !important;
        min-width: 3rem !important;
        padding: 0 !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        cursor: pointer !important;
        transition: all 0.2s ease !important;
        border-radius: 4px !important;
        flex-shrink: 0;
      }

      .paste-phone-button:hover {
        background: #06b6d4 !important;
        border-color: #06b6d4 !important;
        transform: scale(1.05);
      }

      .paste-phone-button:active {
        transform: scale(0.95);
      }

      .paste-phone-button .pi {
        font-size: 1rem !important;
      }

      /* Forçando altura igual para todos os elementos de input */
      :host ::ng-deep .cpf-input-group > *,
      :host ::ng-deep .cep-input-group > *,
      :host ::ng-deep .phone-input-group > * {
        height: 3rem !important;
      }

      /* Estilos adicionais para garantir altura consistente */
      :host ::ng-deep .form-field input[type="text"],
      :host ::ng-deep .form-field input[type="email"],
      :host ::ng-deep .form-field input[type="date"],
      :host ::ng-deep .form-field .p-inputmask,
      :host ::ng-deep .form-field .p-inputtext {
        height: 3rem !important;
        min-height: 3rem !important;
        box-sizing: border-box;
      }

        .same-address-button-container {
      display: flex;
      justify-content: flex-end;
      margin-bottom: 16px;
      padding: 0 16px;
      gap: 8px;
      flex-wrap: wrap;
    }

    .same-address-button {
      display: flex;
      align-items: center;
      gap: 8px;
      border-radius: 4px;
      padding: 8px 16px;
      font-weight: 500;
      transition: background-color 0.3s;
    }


      /* Responsivo */
      @media (max-width: 1024px) {
        .form-row {
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
        }

        .form-row:has(.small) {
          grid-template-columns: 1fr 1fr;
        }

        .form-field.small {
          max-width: none;
        }

        .form-field {
          min-height: 80px;
        }
      }

      @media (max-width: 768px) {
        .form-row {
          grid-template-columns: 1fr;
          gap: 0.75rem;
        }

        .form-row:has(.small) {
          grid-template-columns: 1fr;
        }

        .form-field {
          min-height: 78px;
        }

        .card-header {
          padding: 12px 12px 0;
        }

        .card-header h2 {
          font-size: 1.25rem;
        }

        :host ::ng-deep .compact-panel .p-panel-header {
          padding: 0.5rem 0.75rem;
          font-size: 0.875rem;
        }

        :host ::ng-deep .compact-panel .p-panel-content {
          padding: 0.75rem;
        }

        .form-actions {
          flex-direction: column;
        }
      }
    `,
  ],
})
export class PeopleComponent implements OnInit, OnDestroy, AfterViewInit {
  // Propriedades essenciais apenas
  private cpfSubscription?: Subscription;
  private hasProcessedInitialCpf = false;

  peopleForm: FormGroup;
  person: any = null;
  age: string = '';

  // Propriedades para controlar exibição dos botões de colar - NOVO
  hasLastHomePhone: boolean = false;
  hasLastMobilePhone: boolean = false;

  @ViewChild('cpf') cpfInputField!: ElementRef;
  @ViewChild('zipcode') zipcodeInputField!: ElementRef;
  @ViewChild('birthDate') birthDateInput!: ElementRef;
  @ViewChild('nameInput', { read: ElementRef, static: false }) nameInput!: ElementRef<HTMLInputElement>;
  @ViewChild('mobilePhone', { read: ElementRef, static: false }) mobilePhoneInput!: ElementRef<HTMLInputElement>;

  constructor(
    private cpfSharedService: CpfSharedService,
    private messageService: MessageService,
    private peopleService: PeopleService,
    private addressService: AddressService,
    private fb: FormBuilder,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private formStateService: FormStateService,
    private phoneStateService: PhoneStateService // NOVO
  ) {
    this.peopleForm = this.fb.group({
      name: ['', Validators.required],
      cpf: ['', [Validators.required, Validators.minLength(11), Validators.maxLength(14)]],
      birthDate: ['', [Validators.required, this.birthDateValidator.bind(this)]],
      homePhone: [''],
      mobilePhone: ['', Validators.required],
      email: ['', Validators.email],
      street: ['', Validators.required],
      number: ['', Validators.required],
      compliment: [''],
      reference: [''],
      neighborhood: ['', Validators.required],
      city: ['', Validators.required],
      state: ['', Validators.required],
      zipcode: ['', [Validators.required]],
    });
  }

  ngOnInit() {
    // Subscription simplificada do CPF
    this.cpfSubscription = this.cpfSharedService.currentCpf.subscribe(cpf => {
      if (cpf && !this.hasProcessedInitialCpf) {
        this.hasProcessedInitialCpf = true;
        this.peopleForm.patchValue({ cpf: this.formatCpfForDisplay(cpf) });
        setTimeout(() => this.cpfSharedService.clearCpf(), 100);
      }
    });

    // Verificar se há telefones salvos - NOVO
    this.checkSavedPhones();
  }

  ngOnDestroy() {
    if (this.cpfSubscription) {
      this.cpfSubscription.unsubscribe();
    }
  }

  ngAfterViewInit() {
    // Focar automaticamente no campo CPF ao carregar o componente
    this.focusCpfField();
  }

  // ============= MÉTODOS DE TELEFONE (NOVOS) =============

  /**
   * Verifica se há telefones salvos e atualiza flags de exibição
   */
  private checkSavedPhones(): void {
    this.hasLastHomePhone = this.phoneStateService.hasHomePhone();
    this.hasLastMobilePhone = this.phoneStateService.hasMobilePhone();
  }

  /**
   * Cola o telefone fixo do último cadastro
   */
  pasteHomePhone(): void {
    const lastPhone = this.phoneStateService.getLastHomePhone();
    if (lastPhone) {
      const formatted = this.phoneStateService.formatHomePhone(lastPhone);
      this.peopleForm.patchValue({ homePhone: formatted });
      
      this.messageService.add({
        severity: 'success',
        summary: 'Telefone Colado',
        detail: `Telefone do último cadastro: ${formatted}`,
        life: 2000
      });
    }
  }

  /**
   * Cola o celular do último cadastro
   */
  pasteMobilePhone(): void {
    const lastPhone = this.phoneStateService.getLastMobilePhone();
    if (lastPhone) {
      const formatted = this.phoneStateService.formatMobilePhone(lastPhone);
      this.peopleForm.patchValue({ mobilePhone: formatted });
      
      this.messageService.add({
        severity: 'success',
        summary: 'Celular Colado',
        detail: `Celular do último cadastro: ${formatted}`,
        life: 2000
      });
    }
  }

  /**
   * Retorna o telefone formatado para exibir no tooltip
   */
  getLastHomePhoneFormatted(): string {
    const phone = this.phoneStateService.getLastHomePhone();
    return phone ? this.phoneStateService.formatHomePhone(phone) : '';
  }

  /**
   * Retorna o celular formatado para exibir no tooltip
   */
  getLastMobilePhoneFormatted(): string {
    const phone = this.phoneStateService.getLastMobilePhone();
    return phone ? this.phoneStateService.formatMobilePhone(phone) : '';
  }

  getLastSavedAddress(): any {
  return this.formStateService.getFormData('saved_address');
}

formatAddressForDisplay(): string {
  const savedAddress = this.getLastSavedAddress();
  
  if (!savedAddress) {
    return 'Nenhum endereço salvo';
  }

  // Monta uma string formatada com os campos de endereço
  const parts = [];
  
  if (savedAddress.street) parts.push(savedAddress.street);
  if (savedAddress.number) parts.push(savedAddress.number);
  if (savedAddress.complement) parts.push(savedAddress.complement);
  if (savedAddress.neighborhood) parts.push(savedAddress.neighborhood);
  if (savedAddress.city) parts.push(savedAddress.city);
  if (savedAddress.state) parts.push(savedAddress.state);
  if (savedAddress.zipCode) parts.push(`CEP: ${savedAddress.zipCode}`);

  return parts.length > 0 ? parts.join(', ') : 'Endereço vazio';
}


  // ============= FIM DOS MÉTODOS DE TELEFONE =============

  private focusCpfField() {
    // Tentativa 1: Usando ViewChild
    setTimeout(() => {
      if (this.cpfInputField?.nativeElement) {
        this.cpfInputField.nativeElement.focus();
        return;
      }
      
      // Tentativa 2: Buscar manualmente por ID
      const cpfElement = document.getElementById('cpf');
      if (cpfElement) {
        cpfElement.focus();
        return;
      }
      
      // Tentativa 3: Buscar por query selector
      const cpfBySelector = document.querySelector('p-inputmask[formControlName="cpf"] input') as HTMLInputElement;
      if (cpfBySelector) {
        cpfBySelector.focus();
        return;
      }
    }, 200);
    
    // Tentativa adicional após mais tempo
    setTimeout(() => {
      if (document.activeElement?.id !== 'cpf') {
        const cpfElement = document.querySelector('p-inputmask[formControlName="cpf"] input') as HTMLInputElement;
        if (cpfElement) {
          cpfElement.focus();
        }
      }
    }, 500);
  }

  // Método auxiliar para formatar CPF
  private formatCpfForDisplay(cpf: string): string {
    if (!cpf) return '';
    const cleanCpf = cpf.replace(/\D/g, '');
    if (cleanCpf.length === 11) {
      return cleanCpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    }
    return cpf;
  }

  isFieldInvalid(field: string): boolean {
    const control = this.peopleForm.get(field);
    return !!(control?.invalid && (control.touched || control.dirty));
  }

  // Método para cancelar operação
  cancelar() {
    this.router.navigate(['/home']);
  }

  // Método para novo cadastro após sucesso
  novoCadastro() {
    this.person = null;
    this.peopleForm.reset();
    // Recarregar informações de telefones salvos
    this.checkSavedPhones();
  }

  calculateAge(): void {
    const birthDate = this.peopleForm.get('birthDate')?.value;
    if (birthDate) {
      const today = new Date();
      const birth = new Date(birthDate);
      const ageInYears = today.getFullYear() - birth.getFullYear();
      const monthDiff = today.getMonth() - birth.getMonth();
      
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
        this.age = (ageInYears - 1).toString();
      } else {
        this.age = ageInYears.toString();
      }
    }
  }

  buscarCpf() {
    const cpf = this.peopleForm.get('cpf')?.value;
    const formattedCpf = cpf.replace(/[^0-9]/g, '');

    if (formattedCpf && formattedCpf.length === 11) {
      if (!CpfValidator.validate(formattedCpf)) {
        this.messageService.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'O CPF informado é inválido'
        });
        setTimeout(() => {
          this.cpfInputField.nativeElement.focus();
        }, 0);
      } else {
        this.consultarCPfnoBanco(formattedCpf);
      }
    } else {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção', 
        detail: 'O CPF informado está incompleto'
      });
      setTimeout(() => {
        this.cpfInputField.nativeElement.focus();
      }, 0);
    }
  }

  private consultarCPfnoBanco(formattedCpf: string): void {
    this.peopleService.getPeopleByCpf(formattedCpf).subscribe({
      next: (response) => {
        if (response.data && response.data.length > 0) {
          const personData = response.data[0];
          this.peopleForm.patchValue({
            name: personData.name,
            cpf: personData.cpf,
            birthDate: personData.birthDate,
            email: personData.email,
            homePhone: personData.homePhone,
            mobilePhone: personData.mobilePhone,
            street: personData.address.street,
            number: personData.address.number,
            compliment: personData.address.compliment,
            neighborhood: personData.address.neighborhood,
            city: personData.address.city,
            state: personData.address.state,
            zipcode: personData.address.zipcode,
          });
          
          this.messageService.add({
            severity: 'success',
            summary: 'Sucesso',
            detail: 'Dados encontrados! Pessoa já cadastrada no sistema.'
          });
          
          setTimeout(() => this.focusNextFieldAfterCpfSearch(), 100);
        } else {
          this.fetchDataByCpf(formattedCpf);
        }
      },
      error: (error) => {
        this.fetchDataByCpf(formattedCpf);
      },
    });
  }

  private fetchDataByCpf(cpf: string): void {
    this.peopleService.getDataByCpf(cpf).subscribe({
      next: (response: PeopleBarramentoResponse) => {
        if (response.error === true) {
          setTimeout(() => this.focusNameField(), 0);
          this.messageService.add({
            severity: 'info',
            summary: 'Informação',
            detail: 'CPF Não Cadastrado no SICRED e NÃO é Habilitada. Preencha todos os dados manualmente'
          });
          
          this.peopleForm.patchValue({
            name: '',
            birthDate: ''
          });
          return;
        }

        this.peopleForm.patchValue({
          name: response.nome,
          birthDate: response.dataNascimento,
        });
        setTimeout(() => this.focusMobilePhoneField(), 0);
        this.messageService.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: 'A pessoa não tem cadastro no SICRED, mas é Habilitada. Preencha o restante dos dados manualmente'
        });
      },
      error: (error) => {
        if (error.status === 404) {
          this.messageService.add({
            severity: 'warn',
            summary: 'Aviso',
            detail: 'Nada Encontrado no barramento!'
          });
        } else {
          setTimeout(() => this.focusNameField(), 0);
          this.messageService.add({
            severity: 'warn',
            summary: 'Aviso',
            detail: 'A pessoa não tem cadastro no SICRED, e NÃO é Habilitada. Preencha TODOS os dados manualmente.'
          });
        }
      },
    });
  }

  buscarCep() {
    const zipcode = this.peopleForm.get('zipcode')?.value;
    if (zipcode && zipcode.length === 9) {
      this.fetchAddressByZipcode(zipcode);
    } else {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção',
        detail: 'CEP deve ter 8 dígitos'
      });
    }
  }

  private fetchAddressByZipcode(zipcode: string): void {
    const cleanZipcode = zipcode.replace(/\D/g, '');
    
    if (cleanZipcode.length !== 8) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção',
        detail: 'CEP deve ter exatamente 8 dígitos'
      });
      return;
    }

    this.addressService.getAddressByZipcode(cleanZipcode).subscribe({
      next: (response: ZipcodeResponse) => {
        if (response.logradouro) {
          this.peopleForm.patchValue({
            street: response.logradouro,
            neighborhood: response.bairro,
            city: response.localidade,
            state: response.uf,
          });
          
          this.messageService.add({
            severity: 'success',
            summary: 'Sucesso',
            detail: 'Endereço encontrado e preenchido automaticamente!'
          });
          
          setTimeout(() => {
            const numberField = document.getElementById('number') as HTMLInputElement;
            if (numberField) {
              numberField.focus();
            }
          }, 100);
        } else {
          this.messageService.add({
            severity: 'warn',
            summary: 'Aviso',
            detail: 'CEP não encontrado. Preencha o endereço manualmente.'
          });
        }
      },
      error: (error) => {
        console.error('Erro ao buscar CEP:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Erro ao verificar CEP. Tente novamente ou preencha manualmente.'
        });
      },
    });
  }

  onSubmit() {
    const cpfValue = this.peopleForm.value.cpf.replace(/[^0-9]/g, '');

    if (!CpfValidator.validate(cpfValue)) {
      this.messageService.add({
        severity: 'error',
        summary: 'Erro',
        detail: 'O CPF informado é inválido'
      });
      setTimeout(() => {
        this.cpfInputField.nativeElement.focus();
      }, 0);
      return;
    }

    this.peopleForm.controls['homePhone'].setValue(
      this.cleanPhoneNumber(this.peopleForm.controls['homePhone'].value)
    );
    this.peopleForm.controls['mobilePhone'].setValue(
      this.cleanPhoneNumber(this.peopleForm.controls['mobilePhone'].value)
    );

    if (this.peopleForm.valid) {
      // NOVO: Salvar telefones antes de enviar
      const homePhone = this.peopleForm.value.homePhone;
      const mobilePhone = this.peopleForm.value.mobilePhone;
      
      if (homePhone || mobilePhone) {
        this.phoneStateService.savePhones(homePhone, mobilePhone);
      }

      const payload = {
        people: this.peopleForm.value,
        address: {
          street: this.peopleForm.value.street,
          number: this.peopleForm.value.number,
          compliment: this.peopleForm.value.compliment,
          reference: this.peopleForm.value.reference,
          neighborhood: this.peopleForm.value.neighborhood,
          city: this.peopleForm.value.city,
          state: this.peopleForm.value.state,
          zipcode: this.peopleForm.value.zipcode,
        },
      };

      this.formStateService.saveFormData('saved_address', payload.address);

      this.peopleService.getPeopleByCpf(cpfValue).subscribe({
        next: (response) => {
          if (response.data && response.data.length > 0) {
            this.updatePeople(payload);
          } else {
            this.createPeople(payload);
          }
        },
        error: (error) => {
          this.createPeople(payload);
        },
      });
    } else {
      this.peopleForm.markAllAsTouched();
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção',
        detail: 'Por favor, preencha todos os campos obrigatórios'
      });
    }
  }

  getFormErrors() {
    let formErrors: any = {};
    
    Object.keys(this.peopleForm.controls).forEach(field => {
      const control = this.peopleForm.get(field);
      if (control && !control.valid && (control.dirty || control.touched)) {
        formErrors[field] = control.errors;
      }
    });
    
    return formErrors;
  }

  updatePeople(payload: any) {
    this.peopleService.setPeopleData(payload).subscribe({
      next: (response) => {
        this.messageService.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: 'Dados atualizados com sucesso!'
        });
        this.peopleForm.reset();
        this.router.navigateByUrl('/home/emission');
      },
      error: (error) => {
        console.error('Erro:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Erro ao atualizar. Tente novamente.'
        });
      },
    });
  }

  createPeople(payload: any) {
    this.peopleService.setPeopleData(payload).subscribe({
      next: (response) => {
        this.messageService.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: 'Pessoa cadastrada com sucesso!'
        });

        setTimeout(() => {
          this.cpfSharedService.setCpfCadastrado(this.cleanCpf(this.peopleForm.value.cpf));
        }, 500);

        this.peopleForm.reset();
        
        setTimeout(() => {
          this.router.navigateByUrl('/home/emission');
        }, 1000);
      },
      error: (error) => {
        console.error('Erro:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Erro ao cadastrar. Tente novamente.'
        });
      },
    });
  }

  private cleanCpf(cpf: string): string {
    return cpf ? cpf.replace(/\D/g, '') : '';
  }

  private cleanPhoneNumber(phone: string): string {
    return phone ? phone.replace(/\D/g, '') : '';
  }

  private focusNextFieldAfterCpfSearch(): void {
    if (!this.peopleForm.get('name')?.value) {
      this.focusNameField();
    } else if (!this.peopleForm.get('birthDate')?.value) {
      setTimeout(() => {
        if (this.birthDateInput?.nativeElement) {
          this.birthDateInput.nativeElement.focus();
        }
      }, 100);
    } else if (!this.peopleForm.get('mobilePhone')?.value) {
      this.focusMobilePhoneField();
    } else {
      this.focusNameField();
    }
  }

  private focusNameField(): void {
    setTimeout(() => {
      if (this.nameInput?.nativeElement) {
        const element = this.nameInput.nativeElement;
        element.focus();
        element.select();
      } else {
        const manualElement = document.getElementById('name') as HTMLInputElement;
        if (manualElement) {
          manualElement.focus();
          manualElement.select();
        }
      }
    }, 100);
  }

  private focusMobilePhoneField(): void {
    setTimeout(() => {
      if (this.mobilePhoneInput?.nativeElement) {
        const element = this.mobilePhoneInput.nativeElement;
        element.focus();
        element.select();
      } else {
        const manualElement = document.getElementById('mobilePhone') as HTMLInputElement;
        if (manualElement) {
          manualElement.focus();
          manualElement.select();
        }
      }
    }, 100);
  }

  birthDateValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return null;
    }

    const birthDate = new Date(control.value);
    const today = new Date();
    const age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (birthDate > today) {
      return { futureDate: true };
    }

    if (age > 120 || (age === 120 && monthDiff > 0)) {
      return { minAge: true };
    }

    return null;
  }

   fillWithSavedAddress(): void {
    // Tenta recuperar os dados do endereço do beneficiário salvo
    const beneficiaryAddress = this.formStateService.getFormData('saved_address');

    // console.log('Buscando endereço do beneficiário pela chave: saved_address');
    // console.log('Dados encontrados:', beneficiaryAddress);

    if (beneficiaryAddress) {
      // Preenche o formulário com os dados do beneficiário
      this.peopleForm.patchValue(beneficiaryAddress);

      this.messageService.add({
        severity: 'success',
        summary: 'Sucesso',
        detail: 'Endereço carregado!'
      });
    } else {
      // Se não encontrar dados salvos
      this.messageService.add({
        severity: 'info',
        summary: 'Informação',
        detail: 'Nenhum endereço salvo encontrado.'
      });
    }
  }

}
